import React, { useState, useRef, useEffect } from 'react';
import { usePaymentsFilter } from '../../hooks/usePaymentsFilter';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from '../shared/AppSelect';

const filterSelectTrigger = APP_SELECT_FILTER_TRIGGER;

/**
 * Compact icon-style filter bar for Payments page
 * Provides quick access to filters with minimal space usage
 */
const CompactPaymentsFilter = ({ 
  parties = [], 
  onFiltersChange,
  className = '',
  paymentsFilter = null // Accept external filter instance
}) => {
  // Always call the hook, but use external instance if provided
  const defaultFilterInstance = usePaymentsFilter();
  const filterInstance = paymentsFilter || defaultFilterInstance;
  
  const {
    // Filter states
    timeFilter,
    setTimeFilter,
    customDateFrom,
    setCustomDateFrom,
    customDateTo,
    setCustomDateTo,
    selectedFinancialYear,
    setSelectedFinancialYear,
    selectedMonth,
    setSelectedMonth,
    selectedQuarter,
    setSelectedQuarter,
    selectedSubFilter,
    setSelectedSubFilter,
    selectedParty,
    setSelectedParty,
    partySearchTerm,
    setPartySearchTerm,
    selectedPaymentType,
    setSelectedPaymentType,
    selectedPaymentMode,
    setSelectedPaymentMode,
    selectedReceiptType,
    setSelectedReceiptType,
    amountRange,
    setAmountRange,
    outstandingFilter,
    setOutstandingFilter,
    statusFilter,
    setStatusFilter,
    showAdvancedFilters,
    setShowAdvancedFilters,
    quickFilters,
    setQuickFilters,
    
    // Computed values
    getActiveFilterCount,
    getFilterSummary,
    resetFilters
  } = filterInstance;

  const [isExpanded, setIsExpanded] = useState(false);
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showAdvancedDropdown, setShowAdvancedDropdown] = useState(false);
  const [partySearchInput, setPartySearchInput] = useState('');
  const [filteredParties, setFilteredParties] = useState(parties);
  const [selectedPartyIndex, setSelectedPartyIndex] = useState(-1);

  const partyDropdownRef = useRef(null);
  const dateDropdownRef = useRef(null);
  const advancedDropdownRef = useRef(null);

  // Filter parties based on search input
  useEffect(() => {
    if (!parties || !Array.isArray(parties)) {
      setFilteredParties([]);
      return;
    }
    
    if (partySearchInput.trim() === '') {
      setFilteredParties(parties);
    } else {
      const searchLower = partySearchInput.toLowerCase();
      setFilteredParties(
        parties.filter(party => 
          (party.firmName || party.name || '').toLowerCase().includes(searchLower)
        )
      );
    }
    setSelectedPartyIndex(-1); // Reset selection when search changes
  }, [partySearchInput, parties]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (partyDropdownRef.current && !partyDropdownRef.current.contains(event.target)) {
        setShowPartyDropdown(false);
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
      if (advancedDropdownRef.current && !advancedDropdownRef.current.contains(event.target)) {
        setShowAdvancedDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate financial years
  const financialYears = Array.from({ length: 11 }, (_, i) => {
    const year = new Date().getFullYear() - 5 + i;
    return { value: year.toString(), label: `${year}-${year + 1}` };
  });

  // Generate months
  const months = [
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' }
  ];

  // Generate quarters
  const quarters = [
    { value: '1', label: 'Q1 (Apr-Jun)' },
    { value: '2', label: 'Q2 (Jul-Sep)' },
    { value: '3', label: 'Q3 (Oct-Dec)' },
    { value: '4', label: 'Q4 (Jan-Mar)' }
  ];

  // Payment modes
  const paymentModes = [
    { value: 'all', label: 'All Modes' },
    { value: 'Cash', label: 'Cash' },
    { value: 'UPI', label: 'UPI' },
    { value: 'Bank', label: 'Bank Transfer' },
    { value: 'Cheque/DD', label: 'Cheque/DD' }
  ];

  // Payment types
  const paymentTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'bill', label: 'Bill Payment' },
    { value: 'khata', label: 'Khata Payment' }
  ];

  // Receipt types
  const receiptTypes = [
    { value: 'all', label: 'All Receipts' },
    { value: 'invoice', label: 'Invoice Receipts' },
    { value: 'challan', label: 'Challan Receipts' },
    { value: 'purchase', label: 'Purchase Receipts' }
  ];

  // Outstanding filters
  const outstandingFilters = [
    { value: 'all', label: 'All Bills' },
    { value: 'outstanding', label: 'Outstanding Only' },
    { value: 'paid', label: 'Fully Paid' },
    { value: 'partial', label: 'Partially Paid' }
  ];

  // Handle quick filter toggle
  const handleQuickFilterToggle = (filterName) => {
    console.log('Quick filter toggled:', filterName);
    setQuickFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
    if (onFiltersChange) {
      console.log('Calling onFiltersChange for quick filter');
      onFiltersChange();
    }
  };

  // Handle party selection
  const handlePartySelect = (partyId) => {
    console.log('Party selected:', partyId);
    setSelectedParty(partyId);
    setShowPartyDropdown(false);
    setPartySearchInput('');
    setSelectedPartyIndex(-1);
    if (onFiltersChange) {
      console.log('Calling onFiltersChange');
      onFiltersChange();
    }
  };

  // Handle keyboard navigation in party dropdown
  const handlePartyKeyDown = (e) => {
    if (!showPartyDropdown) return;

    const totalItems = filteredParties.length + 1; // +1 for "All Parties"

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedPartyIndex(prev => (prev + 1) % totalItems);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedPartyIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedPartyIndex === 0) {
          handlePartySelect('');
        } else if (selectedPartyIndex > 0) {
          const party = filteredParties[selectedPartyIndex - 1];
          if (party) handlePartySelect(party.id);
        }
        break;
      case 'Escape':
        setShowPartyDropdown(false);
        setSelectedPartyIndex(-1);
        break;
    }
  };

  // Get selected party name
  const getSelectedPartyName = () => {
    if (!selectedParty) return 'All Parties';
    const party = parties.find(p => p.id === selectedParty);
    return party ? (party.firmName || party.name) : 'Unknown Party';
  };

  // Get date filter display text
  const getDateFilterText = () => {
    if (quickFilters.today) return 'Today';
    if (quickFilters.thisWeek) return 'This Week';
    if (quickFilters.thisMonth) return 'This Month';
    if (quickFilters.thisQuarter) return 'This Quarter';
    if (quickFilters.thisYear) return 'This Year';
    if (quickFilters.last30Days) return 'Last 30 Days';
    if (quickFilters.last90Days) return 'Last 90 Days';
    if (timeFilter === 'custom' && customDateFrom && customDateTo) {
      return `${customDateFrom} to ${customDateTo}`;
    }
    if (timeFilter === 'financial' && selectedFinancialYear) {
      if (selectedSubFilter === 'month' && selectedMonth) {
        const month = months.find(m => m.value === selectedMonth);
        return month ? `${month.label} ${selectedFinancialYear}` : 'Month';
      }
      if (selectedSubFilter === 'quarter' && selectedQuarter) {
        const quarter = quarters.find(q => q.value === selectedQuarter);
        return quarter ? `${quarter.label} ${selectedFinancialYear}` : 'Quarter';
      }
      return `${selectedFinancialYear}-${parseInt(selectedFinancialYear) + 1}`;
    }
    return 'All Time';
  };

  const activeFilterCount = getActiveFilterCount();
  const filterSummary = getFilterSummary();

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-slate-200/80 ${className}`}>
      {/* Compact Filter Bar */}
      <div className="p-3">
        <div className="flex items-center justify-between">
          {/* Left side - Filter Icons */}
          <div className="flex items-center gap-2">
            {/* Date Filter */}
            <div className="relative" ref={dateDropdownRef}>
              <button
                onClick={() => setShowDateDropdown(!showDateDropdown)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  timeFilter !== 'all' || Object.values(quickFilters).some(Boolean)
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                }`}
                title="Date Filter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="hidden sm:inline">{getDateFilterText()}</span>
                <svg className={`w-3 h-3 transition-transform duration-200 ${showDateDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Date Dropdown */}
              {showDateDropdown && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 p-4">
                  <div className="space-y-4">
                    {/* Quick Filters */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Filters</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(quickFilters).map(([key, active]) => (
                          <button
                            key={key}
                            onClick={() => handleQuickFilterToggle(key)}
                            className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                              active
                                ? 'bg-blue-100 text-blue-800 border border-blue-200'
                                : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                            }`}
                          >
                            {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Date Range */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Custom Range</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          value={customDateFrom}
                          onChange={(e) => {
                            setCustomDateFrom(e.target.value);
                            setTimeFilter('custom');
                            if (onFiltersChange) onFiltersChange();
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="From"
                        />
                        <input
                          type="date"
                          value={customDateTo}
                          onChange={(e) => {
                            setCustomDateTo(e.target.value);
                            setTimeFilter('custom');
                            if (onFiltersChange) onFiltersChange();
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="To"
                        />
                      </div>
                    </div>

                    {/* Financial Year */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Financial Year</h4>
                      <AppSelect
                        value={selectedFinancialYear}
                        onChange={(e) => {
                          setSelectedFinancialYear(e.target.value);
                          setTimeFilter('financial');
                          if (onFiltersChange) onFiltersChange();
                        }}
                        placeholder="Select Year"
                        sheetTitle="Financial year"
                        aria-label="Financial year"
                        options={[
                          { value: '', label: 'Select Year' },
                          ...financialYears.map((year) => ({ value: year.value, label: year.label })),
                        ]}
                        triggerClassName={filterSelectTrigger}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Party Filter */}
            <div className="relative" ref={partyDropdownRef}>
              <button
                onClick={() => setShowPartyDropdown(!showPartyDropdown)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedParty
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                }`}
                title="Party Filter"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="hidden sm:inline max-w-24 truncate">{getSelectedPartyName()}</span>
                <svg className={`w-3 h-3 transition-transform duration-200 ${showPartyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Party Dropdown */}
              {showPartyDropdown && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 p-4">
                  <div className="space-y-3">
                    {/* Search Input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={partySearchInput}
                        onChange={(e) => setPartySearchInput(e.target.value)}
                        onKeyDown={handlePartyKeyDown}
                        placeholder="Type to search party..."
                        className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                      />
                      <svg className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {partySearchInput && (
                        <button
                          onClick={() => setPartySearchInput('')}
                          className="absolute right-2 top-2.5 w-4 h-4 text-gray-400 hover:text-gray-600"
                        >
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Party List */}
                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                      <button
                        onClick={() => handlePartySelect('')}
                        className={`w-full text-left px-3 py-2 rounded-t-md text-sm transition-colors border-b border-gray-100 ${
                          selectedPartyIndex === 0
                            ? 'bg-blue-100 text-blue-800'
                            : !selectedParty
                            ? 'bg-blue-50 text-blue-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          All Parties
                        </div>
                      </button>
                      
                      {/* Loading State */}
                      {(!parties || !Array.isArray(parties) || parties.length === 0) && !partySearchInput && (
                        <div className="text-center text-gray-500 text-sm py-4 px-3">
                          <div className="flex items-center justify-center gap-2">
                            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Loading parties...
                          </div>
                        </div>
                      )}
                      
                      {/* Parties List */}
                      {parties && Array.isArray(parties) && parties.length > 0 && filteredParties.map((party, index) => (
                        <button
                          key={party.id}
                          onClick={() => handlePartySelect(party.id)}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                            index === filteredParties.length - 1 ? 'rounded-b-md' : 'border-b border-gray-100'
                          } ${
                            selectedPartyIndex === index + 1
                              ? 'bg-blue-100 text-blue-800'
                              : selectedParty === party.id
                              ? 'bg-blue-50 text-blue-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span className="truncate">{party.firmName || party.name}</span>
                          </div>
                        </button>
                      ))}
                      
                      {/* No Results */}
                      {filteredParties.length === 0 && partySearchInput && (
                        <div className="text-center text-gray-500 text-sm py-4 px-3">
                          <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          No parties found for "{partySearchInput}"
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Advanced Filters */}
            <div className="relative" ref={advancedDropdownRef}>
              <button
                onClick={() => setShowAdvancedDropdown(!showAdvancedDropdown)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedPaymentType !== 'all' || selectedPaymentMode !== 'all' || 
                  selectedReceiptType !== 'all' || outstandingFilter !== 'all' ||
                  amountRange.min !== '' || amountRange.max !== ''
                    ? 'bg-purple-100 text-purple-800 border border-purple-200'
                    : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                }`}
                title="Advanced Filters"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="hidden sm:inline">Advanced</span>
                <svg className={`w-3 h-3 transition-transform duration-200 ${showAdvancedDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Advanced Dropdown */}
              {showAdvancedDropdown && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 p-4">
                  <div className="space-y-4">
                    {/* Payment Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type</label>
                      <AppSelect
                        value={selectedPaymentType}
                        onChange={(e) => {
                          setSelectedPaymentType(e.target.value);
                          if (onFiltersChange) onFiltersChange();
                        }}
                        sheetTitle="Payment type"
                        aria-label="Payment type"
                        options={paymentTypes}
                        triggerClassName={filterSelectTrigger}
                      />
                    </div>

                    {/* Payment Mode */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                      <AppSelect
                        value={selectedPaymentMode}
                        onChange={(e) => {
                          setSelectedPaymentMode(e.target.value);
                          if (onFiltersChange) onFiltersChange();
                        }}
                        sheetTitle="Payment mode"
                        aria-label="Payment mode"
                        options={paymentModes}
                        triggerClassName={filterSelectTrigger}
                      />
                    </div>

                    {/* Outstanding Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Outstanding Status</label>
                      <AppSelect
                        value={outstandingFilter}
                        onChange={(e) => {
                          setOutstandingFilter(e.target.value);
                          if (onFiltersChange) onFiltersChange();
                        }}
                        sheetTitle="Outstanding status"
                        aria-label="Outstanding status"
                        options={outstandingFilters}
                        triggerClassName={filterSelectTrigger}
                      />
                    </div>

                    {/* Amount Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Amount Range</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          value={amountRange.min}
                          onChange={(e) => {
                            setAmountRange({ ...amountRange, min: e.target.value });
                            if (onFiltersChange) onFiltersChange();
                          }}
                          placeholder="Min"
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <input
                          type="number"
                          value={amountRange.max}
                          onChange={(e) => {
                            setAmountRange({ ...amountRange, max: e.target.value });
                            if (onFiltersChange) onFiltersChange();
                          }}
                          placeholder="Max"
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right side - Filter Status & Actions */}
          <div className="flex items-center gap-2">
            {/* Active Filter Count */}
            {activeFilterCount > 0 && (
              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                {activeFilterCount} active
              </span>
            )}

            {/* Clear All Button */}
            {activeFilterCount > 0 && (
              <button
                onClick={resetFilters}
                className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                title="Clear All Filters"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            {/* Expand/Collapse Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              <svg className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Filter Summary (when expanded) */}
        {isExpanded && filterSummary.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex flex-wrap gap-1">
              {filterSummary.map((summary, index) => (
                <span
                  key={index}
                  className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                >
                  {summary}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompactPaymentsFilter;
